// Extrage datele unei facturi de combustibil dintr-un text OCR brut (bon fiscal).
// Toate regex-urile de mai jos au fost calibrate pe bonuri REALE de la Rompetrol,
// Petrom, Socar si Lukoil (benzina si motorina) - vezi comentariile pentru fiecare caz.
// Cand un camp nu poate fi determinat cu incredere, se lasa gol (string vid) mai
// degraba decat sa ghicim gresit - utilizatorul completeaza manual ce lipseste.

// Prefixe distinctive (nu doar numele complet) - OCR-ul pe hartie termica scapa des
// ultimele litere sau le confunda (ex. "ROMPETROL" citit "ROMPETRO" sau "ROMPETRDL").
// Al treilea element = necesita potrivire EXACTA de cuvant (true) - obligatoriu pentru
// prefixele scurte (MOL, OMV), care altfel s-ar potrivi gresit in interiorul altor
// cuvinte (ex. "MOL" in adresa "STR. MOLDOVEI" de pe un bon Petrom).
const FURNIZORI_CUNOSCUTI = [
  ['ROMPETR', 'ROMPETROL', false],
  ['PETROM', 'PETROM', false],
  ['SOCAR', 'SOCAR', false],
  ['LUKOIL', 'LUKOIL', false],
  ['GAZPROM', 'GAZPROM', false],
  ['MOL', 'MOL', true],
  ['OMV', 'OMV', true],
];

function normalizeazaNumar(str) {
  // Bonurile folosesc fie virgula, fie punctul ca separator zecimal (variaza dupa
  // furnizor/POS, nu e o regula fixa) - normalizam mereu la punct, cerut de
  // campurile <input type="number"> din formular.
  if (!str) return '';
  return str.replace(/\s/g, '').replace(',', '.');
}

export function detecteazaFurnizor(text) {
  const upper = text.toUpperCase();
  for (const [prefix, nume, cuvantExact] of FURNIZORI_CUNOSCUTI) {
    const regex = new RegExp('\\b' + prefix + (cuvantExact ? '\\b' : ''), 'i');
    if (regex.test(upper)) return nume;
  }
  return '';
}

export function detecteazaTipCombustibil(text) {
  const upper = text.toUpperCase();
  // Motorina/Diesel verificat INTAI: denumiri precum "EURO LUK 95" contin cifre de
  // octan care ar putea induce in eroare o detectie de benzina daca am cauta doar
  // dupa "95"/"98" fara sa excludem intai varianta diesel.
  if (/DIESEL|MOTORINA|DIZEL/.test(upper)) return 'Motorina';
  if (/\bGPL\b/.test(upper)) return 'GPL';
  if (/ADBLUE|AD\s*BLUE/.test(upper)) return 'AdBlue';
  if (/BENZINA|FARAPB|F[AĂ]R[AĂ]\s*PB|EURO\s*(LUK|SUPER)?|PREMIUM|\b9[58]\b|\b100\b/.test(upper)) return 'Benzina';
  return '';
}

export function detecteazaNrBon(text) {
  // 1) Eticheta explicita "Numar bon"/"Numar Bon" (vazut la Socar si Lukoil)
  let m = text.match(/NUM[AĂ]R\s*BON\s*[:\s]\s*([0-9]+)/i);
  if (m) return m[1];

  // 2) Fara eticheta explicita, dar numarul apare curat pe una din liniile de dupa
  //    titlul "BON FISCAL" (vazut la Rompetrol - "BON FISCAL" urmat de "7001074284")
  const linii = text.split('\n').map(l => l.trim()).filter(Boolean);
  const idxTitlu = linii.findIndex(l => /^BON\s*FISCAL$/i.test(l));
  if (idxTitlu >= 0) {
    for (let i = idxTitlu + 1; i < Math.min(idxTitlu + 4, linii.length); i++) {
      const mm = linii[i].match(/^[A-Z]{0,3}\s*([0-9]{6,15})$/);
      if (mm) return mm[1];
    }
  }
  return ''; // ex. Petrom - nu are un camp "numar bon" identificabil clar pe bon
}

export function detecteazaData(text) {
  // 1) Cu eticheta explicita "DATA" (Rompetrol, unele bonuri Lukoil)
  let m = text.match(/DATA\s*[:\s]\s*(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})/i);
  // 2) Fara eticheta - un tipar zi-luna-an "liber" pe bon, langa ora (Petrom, Socar)
  if (!m) m = text.match(/\b(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})\b/);
  if (!m) return '';

  let [, zi, luna, an] = m;
  if (an.length === 2) an = '20' + an;
  zi = zi.padStart(2, '0');
  luna = luna.padStart(2, '0');
  // Validare minimala - daca valorile sunt in afara intervalului plauzibil, renuntam
  // (mai bine camp gol decat o data gresita, preluata dintr-un alt numar de pe bon).
  if (+zi < 1 || +zi > 31 || +luna < 1 || +luna > 12) return '';
  return `${an}-${luna}-${zi}`;
}

export function detecteazaCantitatePret(text) {
  // Cautam tiparul NUMAR [L]? [xX] NUMAR [L]? - cei doi operanzi ai inmultirii
  // cantitate x pret. Ordinea DIFERA intre furnizori (la Lukoil e uneori
  // "pret X cantitate", nu "cantitate X pret"), deci nu ne bazam pe pozitie, ci pe:
  //   a) numarul urmat direct de litera "L" e mereu cantitatea (semnal principal)
  //   b) daca "L" lipseste (OCR imperfect), cantitatea are mereu MAI MULTE zecimale
  //      decat pretul, care are constant 2 zecimale pe toate bonurile vazute
  const linii = text.split('\n');
  for (const linie of linii) {
    const m = linie.match(/(\d+[.,]\d+)\s*(L)?\s*[xX]\s*(\d+[.,]\d+)\s*(L)?/);
    if (!m) continue;
    const [, num1, l1, num2, l2] = m;
    let cantitateStr, pretStr;
    if (l1 && !l2) { cantitateStr = num1; pretStr = num2; }
    else if (l2 && !l1) { cantitateStr = num2; pretStr = num1; }
    else {
      const zecimale = s => (s.split(/[.,]/)[1] || '').length;
      if (zecimale(num1) >= zecimale(num2)) { cantitateStr = num1; pretStr = num2; }
      else { cantitateStr = num2; pretStr = num1; }
    }
    return { cantitate: normalizeazaNumar(cantitateStr), pretUnitar: normalizeazaNumar(pretStr) };
  }
  return { cantitate: '', pretUnitar: '' };
}

// Functia principala - ruleaza toate detectiile si intoarce campurile gasite.
// Campurile negasite raman string gol si trebuie completate manual de utilizator.
export function parseazaBonCombustibil(textOCR) {
  const { cantitate, pretUnitar } = detecteazaCantitatePret(textOCR);
  return {
    furnizor: detecteazaFurnizor(textOCR),
    nrFactura: detecteazaNrBon(textOCR),
    dataFactura: detecteazaData(textOCR),
    tipCombustibil: detecteazaTipCombustibil(textOCR),
    cantitate,
    pretUnitar,
  };
}
